import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SkillTimeline = () => {
  const [selectedYear, setSelectedYear] = useState(2024);

  const timelineData = [
    {
      year: 2024,
      title: "AI & Machine Learning Integration",
      description: "Expanding into AI-powered applications and machine learning workflows",
      skills: ["TensorFlow", "OpenAI API", "LangChain", "Vector Databases"],
      achievements: [
        "Built AI-powered code review tool",
        "Implemented RAG-based documentation system",
        "Created ML model deployment pipeline"
      ],
      color: "bg-purple-500"
    },
    {
      year: 2023,
      title: "Cloud Architecture & DevOps Mastery",
      description: "Deep dive into cloud-native development and infrastructure automation",
      skills: ["Kubernetes", "Terraform", "AWS Lambda", "Microservices"],
      achievements: [
        "Architected multi-region deployment",
        "Reduced infrastructure costs by 40%",
        "Achieved 99.9% uptime across services"
      ],
      color: "bg-blue-500"
    },
    {
      year: 2022,
      title: "Full-Stack JavaScript Ecosystem",
      description: "Mastering modern JavaScript frameworks and backend technologies",
      skills: ["React 18", "Next.js", "Node.js", "GraphQL"],
      achievements: [
        "Led team of 5 developers",
        "Delivered 3 major product releases",
        "Mentored 2 junior developers"
      ],
      color: "bg-green-500"
    },
    {
      year: 2021,
      title: "Frontend Specialization",
      description: "Focused on creating exceptional user experiences and performance optimization",
      skills: ["React", "TypeScript", "Tailwind CSS", "Webpack"],
      achievements: [
        "Improved app performance by 60%",
        "Built component library used by 10+ teams",
        "Implemented accessibility standards"
      ],
      color: "bg-yellow-500"
    },
    {
      year: 2020,
      title: "Web Development Foundation",
      description: "Started professional development journey with core web technologies",
      skills: ["HTML5", "CSS3", "JavaScript ES6", "Git"],
      achievements: [
        "Completed first commercial project",
        "Built responsive portfolio website",
        "Learned version control best practices"
      ],
      color: "bg-gray-500"
    }
  ];

  return (
    <div className="bg-white rounded-2xl p-8 shadow-soft border border-border">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-text-primary mb-2">Technical Evolution Timeline</h2>
        <p className="text-text-secondary">
          A journey through my technical growth, major milestones, and skill development over the years.
        </p>
      </div>
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Timeline Navigation */}
        <div className="lg:w-1/3">
          <div className="space-y-4">
            {timelineData?.map((item) => (
              <div
                key={item?.year}
                className={`p-4 rounded-xl cursor-pointer transition-all duration-300 ${
                  selectedYear === item?.year
                    ? 'bg-accent text-white shadow-medium'
                    : 'bg-muted hover:bg-muted/80 text-text-primary'
                }`}
                onClick={() => setSelectedYear(item?.year)}
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    selectedYear === item?.year ? 'bg-white' : item?.color
                  }`}></div>
                  <div>
                    <div className="font-semibold">{item?.year}</div>
                    <div className={`text-sm ${
                      selectedYear === item?.year ? 'text-white/80' : 'text-text-secondary'
                    }`}>
                      {item?.title}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline Content */}
        <div className="lg:w-2/3">
          {timelineData?.filter(item => item?.year === selectedYear)?.map((item) => (
              <div key={item?.year} className="animate-fade-in-up">
                <div className="mb-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className={`w-6 h-6 rounded-full ${item?.color}`}></div>
                    <h3 className="text-xl font-bold text-text-primary">{item?.title}</h3>
                    <span className="text-lg font-semibold text-accent">{item?.year}</span>
                  </div>
                  <p className="text-text-secondary leading-relaxed">{item?.description}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Skills Acquired */}
                  <div className="bg-muted rounded-xl p-6">
                    <h4 className="font-semibold text-text-primary mb-4 flex items-center space-x-2">
                      <Icon name="Code2" size={18} />
                      <span>Skills Acquired</span>
                    </h4>
                    <div className="space-y-2">
                      {item?.skills?.map((skill, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2 p-2 bg-white rounded-lg"
                        >
                          <div className={`w-2 h-2 rounded-full ${item?.color}`}></div>
                          <span className="text-text-primary font-medium">{skill}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Key Achievements */}
                  <div className="bg-muted rounded-xl p-6">
                    <h4 className="font-semibold text-text-primary mb-4 flex items-center space-x-2">
                      <Icon name="Trophy" size={18} />
                      <span>Key Achievements</span>
                    </h4>
                    <div className="space-y-3">
                      {item?.achievements?.map((achievement, index) => (
                        <div
                          key={index}
                          className="flex items-start space-x-2 p-2 bg-white rounded-lg"
                        >
                          <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                          <span className="text-text-primary text-sm">{achievement}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Progress Indicator */}
                <div className="mt-6 p-4 bg-accent/5 rounded-xl border border-accent/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-text-primary">Career Progress</span>
                    <span className="text-sm font-semibold text-accent">
                      {Math.round(((2024 - item?.year + 1) / 5) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 bg-accent rounded-full transition-all duration-500"
                      style={{ width: `${((2024 - item?.year + 1) / 5) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default SkillTimeline;